import { awsConfig } from "../config/aws-config";
import { toast } from "react-toastify";
import { Event } from "../../types/event";

/** Payload for creating an event */
interface CreateEventPayload {
  title: string;
  description: string;
  location?: string;
  date: string;
  time?: string;
  organizer?: string;
  imageUrl?: string;
}

/** Generic error parser */
const parseErrorResponse = async (response: Response): Promise<{ message: string }> => {
  try {
    const data = await response.json();
    return { message: data.message || "An error occurred" };
  } catch {
    return { message: response.statusText || "An error occurred" };
  }
};

export const eventService = {
  /**
   * Creates a new event (requires authentication).
   * Endpoint: POST /events
   */
  createEvent: async (token: string, data: CreateEventPayload): Promise<Event> => {
    const url = `${awsConfig.apiEndpoint}/events`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to create event");
      }

      return await response.json();
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Retrieves all events (public endpoint).
   * Endpoint: GET /events
   */
  getEvents: async (): Promise<Event[]> => {
    const url = `${awsConfig.apiEndpoint}/events`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to fetch events");
      }

      const data = await response.json();
      if (!data.success || !Array.isArray(data.events)) {
        throw new Error("Invalid response format from server");
      }

      return data.events;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Retrieves all events with admin privileges (authenticated endpoint).
   * This function accepts ID tokens from Cognito (idToken).
   * Endpoint: GET /events
   */
  getEventsAsAdmin: async (token: string): Promise<Event[]> => {
    const url = `${awsConfig.apiEndpoint}/events`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to fetch events");
      }

      const data = await response.json();
      if (!data.success || !Array.isArray(data.events)) {
        throw new Error("Invalid response format from server");
      }

      return data.events;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Retrieves a single event by ID (public endpoint).
   * Endpoint: GET /events/{eventId}
   */
  getEventById: async (eventId: string): Promise<Event> => {
    const url = `${awsConfig.apiEndpoint}/events/${eventId}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to fetch event");
      }

      const data = await response.json();
      if (!data.success || !data.event) {
        throw new Error("Invalid response format from server");
      }

      return data.event;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Updates an event (requires authentication).
   * Endpoint: PUT /events/{eventId}
   */
  updateEvent: async (token: string, eventId: string, data: Partial<Event>): Promise<Event> => {
    const url = `${awsConfig.apiEndpoint}/events/${eventId}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
        body: JSON.stringify(data),
      });
  
      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to update event");
      }
  
      return await response.json();
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },

  /**
   * Deletes an event (requires authentication).
   * Endpoint: DELETE /events/{eventId}
   */
  deleteEvent: async (token: string, eventId: string): Promise<{ success: boolean }> => {
    const url = `${awsConfig.apiEndpoint}/events/${eventId}`;
    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData.message || "Failed to delete event");
      }
      return await response.json();
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  },
};
